import type { CampaignDraft, FunnelStrategyData, MediaMixConfig } from "./campaign-draft";
import type { Brand } from "@shared/schema";
import { displayUrl, normalizeExampleImage } from "@shared/adImage";

function firstExampleImageUrl(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    const url = displayUrl(normalizeExampleImage(entry));
    if (url) return url;
  }
  return null;
}

const UI_ONLY_KEYS = new Set([
  "selected", "expanded", "isOpen", "hover", "isHovered",
  "isExpanded", "isSelected", "isVisible", "animating",
  "transitionState", "displayMode", "filterState",
  "isFocused", "isDragging", "isDropTarget",
]);

const TIMESTAMP_SKIP_KEYS = new Set([
  "createdAt", "updatedAt", "archivedAt", "created_at", "updated_at", "archived_at",
]);

function camelToSnake(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
}

function deepNormalize(obj: any, skipTimestamps = true): any {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) return obj.map((item) => deepNormalize(item, skipTimestamps));
  if (typeof obj !== "object") return obj;

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (UI_ONLY_KEYS.has(key)) continue;
    if (value === undefined) continue;
    if (skipTimestamps && TIMESTAMP_SKIP_KEYS.has(key)) continue;
    const snakeKey = camelToSnake(key);
    result[snakeKey] = deepNormalize(value, skipTimestamps);
  }
  return result;
}

function hydrateFunnelStrategy(fs: FunnelStrategyData | null): Record<string, any> | null {
  if (!fs) return null;
  const normalized = deepNormalize(fs);
  if (normalized.stages && !normalized.funnel_stages) {
    normalized.funnel_stages = normalized.stages;
    delete normalized.stages;
  }
  return normalized;
}

function hydrateMediaMix(mm: MediaMixConfig | null): Record<string, any> | null {
  if (!mm) return null;
  const normalized = deepNormalize(mm);
  if (normalized.total_units !== undefined && normalized.total_assets === undefined) {
    normalized.total_assets = normalized.total_units;
  }
  if (normalized.static_units !== undefined && normalized.static_count === undefined) {
    normalized.static_count = normalized.static_units;
  }
  if (normalized.video_units !== undefined && normalized.video_count === undefined) {
    normalized.video_count = normalized.video_units;
  }
  return normalized;
}


export interface CampaignPlanPayload {
  product: Record<string, any> | null;
  persona: Record<string, any> | null;
  brand: Record<string, any> | null;
  narrative_opportunity: Record<string, any> | null;
  selected_narrative_angles: Record<string, any>[];
  funnel_strategy: Record<string, any> | null;
  media_mix: Record<string, any> | null;
  ad_unit_selections: Record<string, any>;
}

export interface CampaignPlanDraftFields {
  product: CampaignDraft["product"];
  persona: CampaignDraft["persona"];
  narrativeOpportunity: CampaignDraft["narrativeOpportunity"];
  narrativeAngles: CampaignDraft["narrativeAngles"];
  funnelStrategy: CampaignDraft["funnelStrategy"];
  mediaMix: CampaignDraft["mediaMix"];
  adUnitSelections: CampaignDraft["adUnitSelections"];
  adUnitPlan: CampaignDraft["adUnitPlan"];
}

function hydrateAdUnitSelectionsFromFields(fields: CampaignPlanDraftFields): Record<string, any> {
  const plan = fields.adUnitPlan;
  const selections = fields.adUnitSelections;

  return {
    summary: plan?.summary ?? null,
    stage_plans: plan?.stagePlans
      ? plan.stagePlans.map((sp) => deepNormalize(sp))
      : null,
    selected_units: selections.map((sel) => ({
      ad_unit_id: sel.adUnitId,
      ad_unit_name: sel.adUnit.name,
      format: sel.adUnit.format,
      category: sel.adUnit.category,
      funnel_stage_id: sel.funnelStageId,
      quantity: sel.quantity,
      // User-picked reference image (or first example image as fallback) so the
      // Sim Campaign Plan workflow knows which visual is locked in per stage.
      selected_image_url:
        sel.selectedImageUrl ?? firstExampleImageUrl(sel.adUnit.exampleImages),
    })),
    total_units: selections.reduce((s, u) => s + u.quantity, 0),
    target_total_units: fields.mediaMix?.totalUnits ?? null,
  };
}

export function buildCampaignPlanPayload(
  fields: CampaignPlanDraftFields,
  brand: Brand | null,
): CampaignPlanPayload {
  return {
    product: fields.product ? deepNormalize(fields.product) : null,
    persona: fields.persona ? deepNormalize(fields.persona) : null,
    brand: brand ? deepNormalize(brand) : null,
    narrative_opportunity: fields.narrativeOpportunity
      ? deepNormalize(fields.narrativeOpportunity)
      : null,
    selected_narrative_angles: (fields.narrativeAngles ?? []).map((a) => deepNormalize(a)),
    funnel_strategy: hydrateFunnelStrategy(fields.funnelStrategy),
    media_mix: hydrateMediaMix(fields.mediaMix),
    ad_unit_selections: hydrateAdUnitSelectionsFromFields(fields),
  };
}
